import { useState, useMemo } from 'react';
import { FilterMatchMode } from 'primereact/api';
import {
    useGetAllTeamProductivityMeasurementQuery,
    useCreateTeamProductivityMeasurementMutation,
    useUpdateTeamProductivityMeasurementMutation,
    useCompleteTeamProductivityMeasurementMutation
} from '../../../api/endpoints/teamProductivityMeasurement';
import { useLanguage } from '../../../contexts/LanguageContext';
import toastService from '../../../utils/toastService';
import { matchModeToOperator } from '../../../utils/filterUtils';
import { useNavigate } from 'react-router-dom';

const INITIAL_FILTER_STATE = {
    requestNumber: null,
    requestNumberMatchMode: null,
    requestBroadChannelText: null,
    requestBroadChannelTextMatchMode: null,
    requestTypeText: null,
    requestTypeTextMatchMode: null,
    requestDetail: null,
    requestDetailMatchMode: null,
    requestSenderText: null,
    requestSenderTextMatchMode: null,
    requestSenderName: null,
    requestSenderNameMatchMode: null,
    howItConcluded: null,
    howItConcludedMatchMode: null,
    lessonLearned: null,
    vorVehicle: null,
    vorVehicleText: null,
    startDate: null,
    startDateMatchMode: null,
    endDate: null,
    endDateMatchMode: null,
    createdName: null,
    createdNameMatchMode: null,
    createdDate: null,
    createdDateMatchMode: null,
    status: null,
    oldRequestNumbers: null,
    timeDifference: null,
    timeDifferenceMatchMode: null
};

const backendColumnMap = {
    requestNumber: 'TalepNumarasi',
    requestBroadChannelText: 'TalebinGenisKanal',
    requestTypeText: 'TalepTipi',
    requestDetail: 'TalepDetay',
    requestSenderText: 'TalepGonderen',
    requestSenderName: 'TalepGonderenIsim',
    howItConcluded: 'NasilSonuclandi',
    lessonLearned: 'LessonLearned',
    vorVehicleText: 'VORArac',
    startDate: 'BaslangicTarih',
    endDate: 'BitisTarih',
    timeDifference: 'SureFarki',
    createdName: 'CreatedName',
    createdDate: 'CreatedDate',
    status: 'Status'
};

const getBackendColumnName = (field) => backendColumnMap[field] || null;

const getOperatorValue = (matchMode) => matchModeToOperator[matchMode] || matchModeToOperator[FilterMatchMode.CONTAINS];

const buildDynamicFilters = (filtersState) => {
    const filters = [];
    const dateFields = ['startDate', 'endDate', 'createdDate'];

    const pushFilter = (field, value, matchMode) => {
        if (value === null || value === undefined || value === '') return;
        const column = getBackendColumnName(field);
        if (!column) return;

        let filterValue = value;
        if (dateFields.includes(field) && value instanceof Date) {
            filterValue = value.toISOString();
        } else {
            filterValue = String(value).trim();
            if (filterValue === '') return;
        }

        filters.push({
            column,
            operator: getOperatorValue(matchMode),
            value: filterValue
        });
    };

    pushFilter('requestNumber', filtersState?.requestNumber, filtersState?.requestNumberMatchMode);
    pushFilter('requestBroadChannelText', filtersState?.requestBroadChannelText, filtersState?.requestBroadChannelTextMatchMode);
    pushFilter('requestTypeText', filtersState?.requestTypeText, filtersState?.requestTypeTextMatchMode);
    pushFilter('requestDetail', filtersState?.requestDetail, filtersState?.requestDetailMatchMode);
    pushFilter('requestSenderText', filtersState?.requestSenderText, filtersState?.requestSenderTextMatchMode);
    pushFilter('requestSenderName', filtersState?.requestSenderName, filtersState?.requestSenderNameMatchMode);
    pushFilter('howItConcluded', filtersState?.howItConcluded, filtersState?.howItConcludedMatchMode);
    pushFilter('lessonLearned', filtersState?.lessonLearned, filtersState?.lessonLearnedMatchMode);
    pushFilter('startDate', filtersState?.startDate, filtersState?.startDateMatchMode);
    pushFilter('endDate', filtersState?.endDate, filtersState?.endDateMatchMode);
    pushFilter('createdName', filtersState?.createdName, filtersState?.createdNameMatchMode);
    pushFilter('createdDate', filtersState?.createdDate, filtersState?.createdDateMatchMode);
    pushFilter('status', filtersState?.status, filtersState?.statusMatchMode);
    pushFilter('vorVehicleText', filtersState?.vorVehicleText, filtersState?.vorVehicleTextMatchMode);
    pushFilter('timeDifference', filtersState?.timeDifference, filtersState?.timeDifferenceMatchMode);

    return filters;
};

const buildDynamicSorts = (field, order) => {
    if (!field || order === 0) return null;
    const column = getBackendColumnName(field);
    if (!column) return null;
    return [{ column, direction: order === 1 ? 1 : 2 }];
};

const useTeamProductivityMeasurement = () => {
    const { th, getCurrentLanguageId } = useLanguage();
    const navigate = useNavigate();

    const [lazyParams, setLazyParams] = useState({ first: 0, rows: 10, page: 1 });
    const [sortField, setSortField] = useState(null);
    const [sortOrder, setSortOrder] = useState(0);
    const [activeFilters, setActiveFilters] = useState(INITIAL_FILTER_STATE);

    const languageId = useMemo(() => getCurrentLanguageId(), [getCurrentLanguageId]);

    const queryParams = useMemo(() => {
        const params = {
            pageNumber: lazyParams.page,
            pageSize: lazyParams.rows,
            languageId,
            includeFilterOptions: true,
            filterOptionsColumns: ['TalebinGenisKanal', 'TalepTipi', 'TalepGonderen', 'Status']
        };
        const filters = buildDynamicFilters(activeFilters);
        if (filters?.length > 0) params.filters = filters;
        const sorts = buildDynamicSorts(sortField, sortOrder);
        if (sorts?.length > 0) params.sorts = sorts;
        if (activeFilters?.oldRequestNumbers) params.oldRequestNumber = activeFilters.oldRequestNumbers;
        return params;
    }, [activeFilters, lazyParams.page, lazyParams.rows, languageId, sortField, sortOrder]);

    const { data: apiData, isLoading, isFetching, refetch } = useGetAllTeamProductivityMeasurementQuery(queryParams);

    const [createMutation, { isLoading: isCreating }] = useCreateTeamProductivityMeasurementMutation();
    const [updateMutation, { isLoading: isUpdating }] = useUpdateTeamProductivityMeasurementMutation();
    const [completeMutation, { isLoading: isCompleting }] = useCompleteTeamProductivityMeasurementMutation();

    const handleFilterChange = (updatedFilters) => {
        setActiveFilters(updatedFilters);
        setLazyParams({ first: 0, rows: lazyParams.rows, page: 1 });
    };

    const initFilters = () => {
        setActiveFilters(INITIAL_FILTER_STATE);
        setLazyParams({ first: 0, rows: 10, page: 1 });
    };

    const onPage = (event) => {
        setLazyParams({
            first: event.first,
            rows: event.rows,
            page: event.page + 1
        });
    };

    const handleSortChange = (event) => {
        setSortField(event?.sortField || null);
        setSortOrder(event?.sortOrder || 0);
        setLazyParams({ first: 0, rows: lazyParams.rows, page: 1 });
    };

    const saveRecord = async (data, isEdit) => {
        try {
            const result = isEdit ? await updateMutation(data).unwrap() : await createMutation(data).unwrap();

            if (!result?.isSuccessful) {
                toastService.error(result?.errorMessage, th('common.error'));
                return false;
            }

            toastService.success(isEdit ? th('teamProductivity.messages.updateSuccess') : th('teamProductivity.messages.createSuccess'), th('common.success'));
            refetch();
            return true;
        } catch (error) {
            toastService.error(error?.errorMessage || error?.data?.errorMessage, th('common.error'));
            return false;
        }
    };

    const completeRecord = async (id, endDate) => {
        try {
            const result = await completeMutation({ id, endDate }).unwrap();

            if (!result?.isSuccessful) {
                toastService.error(result?.errorMessage, th('common.error'));
                return false;
            }

            toastService.success(th('teamProductivity.messages.completeSuccess'), th('common.success'));
            refetch();
            return true;
        } catch (error) {
            toastService.error(error?.errorMessage || error?.data?.errorMessage, th('common.error'));
            return false;
        }
    };

    return {
        apiData,
        isLoading,
        isFetching,
        lazyParams,
        sortField,
        sortOrder,
        activeFilters,
        queryParams,
        handleFilterChange,
        initFilters,
        onPage,
        onSort: handleSortChange,
        refetch,
        saveRecord,
        completeRecord,
        isCreating,
        isUpdating,
        isCompleting
    };
};

export default useTeamProductivityMeasurement;
